import React, { useMemo, useState } from "react";
import { Box, Grid, Typography, useTheme, Tabs, Tab } from "@mui/material";
import { useTranslation } from "react-i18next";
import AssignmentIcon from "@mui/icons-material/Assignment";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import BugReportIcon from "@mui/icons-material/BugReport";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import TimerIcon from "@mui/icons-material/Timer";
import TimelineIcon from "@mui/icons-material/Timeline";
import GroupIcon from "@mui/icons-material/Group";
import { ApexOptions } from "apexcharts";
import {
  SeveritySummary,
  SkillSummary,
  StatusSummary,
  TagSummary,
  TypeSummary,
} from "./interface";
import { KPICard } from "./KPICard";
import { ChartCard } from "./ChartCard";
import {
  COLORS_CHART,
  secondsToHours,
  SEVERITY_COLORS,
  STATUS_COLORS,
  formatCodeLabel,
} from "./util";
import SprintDetailView from "./SprintDetailView";

interface ScrumDashboardProps {
  projectSummary: any;
  projectMetrics: any;
  sprints: any[];
  parentData?: any;
  totalUsers: number;
}

const ScrumDashboard: React.FC<ScrumDashboardProps> = ({
  projectSummary,
  projectMetrics,
  sprints,
  parentData,
  totalUsers,
}) => {
  const theme = useTheme();
  const { t } = useTranslation(["bugherd"]);
  const [selectedSprintIndex, setSelectedSprintIndex] = useState(0);
  const assigneeWorkloadData = useMemo(() => {
    if (!projectMetrics?.assigneeVsWorkload) {
      return { categories: [], timeSpent: [], estimated: [] };
    }

    const data = Object.values(projectMetrics.assigneeVsWorkload)
      .map((item: any) => ({
        name: item.workedBy?.fullName || item.assignTo?.fullName || "Unknown",
        timeSpent: secondsToHours(item.totalTimeSpend),
        estimated: secondsToHours(item.timeEstimation || 0),
        id: item.workedBy?._id || item.assignTo?._id,
      }))
      .filter((item) => item.name !== "Unknown" && item.timeSpent > 0)
      .sort((a, b) => b.timeSpent - a.timeSpent)
      .slice(0, 10);

    return {
      categories: data.map((d) => d.name),
      timeSpent: data.map((d) => d.timeSpent),
      estimated: data.map((d) => d.estimated),
    };
  }, [projectMetrics]);

  // ✅ SCRUM SKILL WORKLOAD DATA - Object structure
  const skillWorkloadData = useMemo(() => {
    if (!projectMetrics?.skillSetVsWorkload) {
      return { categories: [], timeSpent: [], estimated: [] };
    }

    const data = Object.values(projectMetrics.skillSetVsWorkload)
      .filter(
        (item: any) =>
          item.skillType !== "Not Defined" && item.totalTimeSpendSkillWise > 0
      )
      .map((item: any) => ({
        name: item.skillType,
        timeSpent: secondsToHours(item.totalTimeSpendSkillWise),
        estimated: secondsToHours(item.timeEstimationSkillWise || 0),
      }))
      .sort((a, b) => b.timeSpent - a.timeSpent);

    return {
      categories: data.map((d) => d.name),
      timeSpent: data.map((d) => d.timeSpent),
      estimated: data.map((d) => d.estimated),
    };
  }, [projectMetrics]);

  // Status data
  const statusChartData = useMemo(() => {
    const bugsByStatus = projectSummary?.byStatus || [];
    return bugsByStatus.map((item: StatusSummary) => item.total);
  }, [projectSummary?.byStatus]);

  const statusLabels = useMemo(() => {
    const bugsByStatus = projectSummary?.byStatus || [];
    return bugsByStatus.map((item: StatusSummary) =>
      formatCodeLabel(item.statusCode)
    );
  }, [projectSummary?.byStatus]);

  const statusColors = useMemo(() => {
    const bugsByStatus = projectSummary?.byStatus || [];
    return bugsByStatus.map(
      (item: StatusSummary) => STATUS_COLORS[item.statusCode] || "#999"
    );
  }, [projectSummary?.byStatus]);

  // Severity data
  const severityChartData = useMemo(() => {
    return (projectSummary?.bySeverity || []).map(
      (item: SeveritySummary) => item.total
    );
  }, [projectSummary?.bySeverity]);

  const severityLabels = useMemo(() => {
    return (projectSummary?.bySeverity || []).map((item: SeveritySummary) =>
      formatCodeLabel(item.severity)
    );
  }, [projectSummary?.bySeverity]);

  const severityColors = useMemo(() => {
    return (projectSummary?.bySeverity || []).map(
      (item: SeveritySummary) => SEVERITY_COLORS[item.severity] || "#999"
    );
  }, [projectSummary?.bySeverity]);

  // Type data
  const typeChartData = useMemo(() => {
    return (projectSummary?.byType || []).map(
      (item: TypeSummary) => item.total
    );
  }, [projectSummary?.byType]);

  const typeLabels = useMemo(() => {
    return (projectSummary?.byType || []).map((item: TypeSummary) =>
      formatCodeLabel(item.type)
    );
  }, [projectSummary?.byType]);

  // Skill data
  const skillChartData = useMemo(() => {
    if (
      !projectSummary?.byRequiredSkillSet ||
      projectSummary.byRequiredSkillSet.length === 0
    )
      return { values: [], labels: [] };

    const processedData = projectSummary.byRequiredSkillSet
      .map((item: SkillSummary) => ({
        name: formatCodeLabel(item.skillSet || "Unknown"),
        value: item.total,
      }))
      .filter(
        (item) =>
          item.name !== "Unknown" &&
          item.name !== "Not Defined" &&
          item.value > 0
      );

    return {
      values: processedData.map((item) => item.value),
      labels: processedData.map((item) => item.name),
    };
  }, [projectSummary?.byRequiredSkillSet]);

  // Tag data
  const tagChartData = useMemo(() => {
    return (projectSummary?.byTags || [])
      .filter((t: TagSummary) => t.tags)
      .slice(0, 8)
      .map((item: TagSummary) => item.total);
  }, [projectSummary?.byTags]);

  const tagLabels = useMemo(() => {
    return (projectSummary?.byTags || [])
      .filter((t: TagSummary) => t.tags)
      .slice(0, 8)
      .map((item: TagSummary) => formatCodeLabel(item.tags));
  }, [projectSummary?.byTags]);

  // Progress data
  const progressData = useMemo(() => {
    const bugsByStatus = projectSummary?.byStatus || [];
    const completedBugs =
      bugsByStatus.find((s: StatusSummary) => s.statusCode === "DONE")?.total ||
      0;
    const totalBugs = projectSummary?.totalBugs || 0;
    const remainingBugs = Math.max(0, totalBugs - completedBugs);

    return [completedBugs, remainingBugs];
  }, [projectSummary?.byStatus, projectSummary?.totalBugs]);

  const progressLabels = [t("Completed"), t("Remaining")];

  // KPIs
  const totalBugs = projectSummary?.totalBugs || 0;
  const closedBugs = projectSummary?.totalClosedBugs || 0;
  const openBugs = totalBugs - closedBugs;
  const bugsByStatus = projectSummary?.byStatus || [];
  const blockedBugs =
    bugsByStatus.find((s: StatusSummary) => s.statusCode === "BLOCKED")
      ?.total || 0;

  // ✅ Calculate metrics from SCRUM data
  const scrumMetrics = useMemo(() => {
    if (!projectMetrics?.projectMetrics) {
      return {
        totalTimeSpent: 0,
        totalEstimated: 0,
        totalSprints: 0,
        totalAssignees: 0,
      };
    }

    return {
      totalTimeSpent: secondsToHours(
        projectMetrics.projectMetrics.totalTimeSpendInProject || 0
      ),
      totalEstimated: secondsToHours(
        projectMetrics.projectMetrics.totalProjectTimeEstimation || 0
      ),
      totalSprints: projectMetrics.projectMetrics.totalSprints || 0,
      totalAssignees: projectMetrics.projectMetrics.totalAssignee || 0,
    };
  }, [projectMetrics]);

  const avgResolutionTime = useMemo(() => {
    if (closedBugs === 0) return 0;
    if (scrumMetrics.totalTimeSpent > 0) {
      return (scrumMetrics.totalTimeSpent / closedBugs).toFixed(1);
    }
    return 0;
  }, [closedBugs, scrumMetrics]);

  // ApexChart Options (same as before - I'll include key ones)
  const mainPieOptions: ApexOptions = useMemo(
    () => ({
      chart: {
        type: "donut",
        width: "100%",
        fontFamily: "inherit",
      },
      labels: statusLabels,
      colors: statusColors,
      legend: {
        show: true,
        position: "bottom",
        horizontalAlign: "center",
        fontSize: "12px",
        fontWeight: 500,
        offsetY: 5,
      },
      dataLabels: {
        enabled: true,
        style: {
          fontSize: "13px",
          fontWeight: "600",
        },
        formatter: function (val: number, opts: any) {
          return opts.w.config.series[opts.seriesIndex];
        },
        dropShadow: {
          enabled: false,
        },
      },
      plotOptions: {
        pie: {
          donut: {
            size: "65%",
            labels: {
              show: true,
              total: {
                show: true,
                label: t("Total"),
                fontSize: "14px",
                fontWeight: 600,
                formatter: function (w: any) {
                  return w.globals.seriesTotals.reduce(
                    (a: number, b: number) => a + b,
                    0
                  );
                },
              },
            },
          },
        },
      },
      tooltip: {
        y: {
          formatter: function (val: number) {
            return val.toString();
          },
        },
      },
      stroke: {
        width: 2,
      },
      responsive: [
        {
          breakpoint: 480,
          options: {
            legend: {
              position: "bottom",
            },
          },
        },
      ],
    }),
    [statusLabels, statusColors]
  );

  const severityBarOptions: ApexOptions = useMemo(
    () => ({
      chart: {
        type: "bar",
        toolbar: { show: false },
        width: "100%",
        fontFamily: "inherit",
      },
      plotOptions: {
        bar: {
          borderRadius: 10,
          distributed: true,
          columnWidth: "70%",
        },
      },
      colors: severityColors,
      xaxis: {
        categories: severityLabels,
        labels: {
          rotate: -45,
          style: {
            fontSize: "11px",
            fontWeight: 500,
          },
        },
      },
      yaxis: {
        labels: {
          style: {
            fontSize: "11px",
          },
          formatter: function (val: number) {
            return Math.floor(val).toString();
          },
        },
      },
      legend: {
        show: false,
      },
      dataLabels: {
        enabled: true,
        style: {
          fontSize: "11px",
          fontWeight: "600",
        },
      },
      tooltip: {
        y: {
          formatter: function (val: number) {
            return val.toString();
          },
          title: {
            formatter: function (seriesName: string) {
              return "";
            },
          },
        },
      },
      grid: {
        borderColor: "#f1f1f1",
        strokeDashArray: 4,
      },
    }),
    [severityLabels, severityColors]
  );

  const typeBarOptions: ApexOptions = useMemo(
    () => ({
      chart: {
        type: "bar",
        toolbar: { show: false },
        width: "100%",
        fontFamily: "inherit",
      },
      plotOptions: {
        bar: {
          borderRadius: 10,
          distributed: true,
          columnWidth: "70%",
        },
      },
      colors: COLORS_CHART,
      xaxis: {
        categories: typeLabels,
        labels: {
          rotate: -45,
          style: {
            fontSize: "11px",
            fontWeight: 500,
          },
        },
      },
      yaxis: {
        labels: {
          style: {
            fontSize: "11px",
          },
          formatter: function (val: number) {
            return Math.floor(val).toString();
          },
        },
      },
      legend: {
        show: false,
      },
      dataLabels: {
        enabled: true,
        style: {
          fontSize: "11px",
          fontWeight: "600",
        },
      },
      tooltip: {
        y: {
          formatter: function (val: number) {
            return val.toString();
          },
          title: {
            formatter: function (seriesName: string) {
              return "";
            },
          },
        },
      },
      grid: {
        borderColor: "#f1f1f1",
        strokeDashArray: 4,
      },
    }),
    [typeLabels]
  );

  const progressOptions: ApexOptions = useMemo(
    () => ({
      chart: {
        type: "donut",
        width: "100%",
        fontFamily: "inherit",
      },
      labels: progressLabels,
      colors: ["#4CAF50", "#FFC107"],
      legend: {
        show: true,
        position: "bottom",
        horizontalAlign: "center",
        fontSize: "12px",
        fontWeight: 500,
        offsetY: 5,
      },
      dataLabels: {
        enabled: true,
        style: {
          fontSize: "13px",
          fontWeight: "600",
        },
        formatter: function (val: number, opts: any) {
          return opts.w.config.series[opts.seriesIndex];
        },
        dropShadow: {
          enabled: false,
        },
      },
      plotOptions: {
        pie: {
          donut: {
            size: "65%",
            labels: {
              show: true,
              total: {
                show: true,
                label: t("Total"),
                fontSize: "14px",
                fontWeight: 600,
                formatter: function (w: any) {
                  return w.globals.seriesTotals.reduce(
                    (a: number, b: number) => a + b,
                    0
                  );
                },
              },
            },
          },
        },
      },
      tooltip: {
        y: {
          formatter: function (val: number) {
            return val.toString();
          },
        },
      },
      stroke: {
        width: 2,
      },
      responsive: [
        {
          breakpoint: 480,
          options: {
            legend: {
              position: "bottom",
            },
          },
        },
      ],
    }),
    [progressLabels]
  );
  //@ts-ignore
  const skillBarOptions: ApexOptions = useMemo(
    () => ({
      chart: {
        type: "bar",
        toolbar: { show: false },
        width: "100%",
        fontFamily: "inherit",
      },
      plotOptions: {
        bar: {
          borderRadius: 8,
          horizontal: true,
          distributed: true,
          barHeight: "75%",
        },
      },
      colors: COLORS_CHART,
      xaxis: {
        title: {
          text: t("Number of Issues"),
          style: {
            fontSize: "12px",
            fontWeight: 600,
          },
        },
        labels: {
          style: {
            fontSize: "11px",
          },
          formatter: function (val: string) {
            return Math.floor(Number(val)).toString();
          },
        },
      },
      yaxis: {
        categories: skillChartData.labels,
        labels: {
          style: {
            fontSize: "11px",
            fontWeight: 500,
          },
        },
      },
      legend: {
        show: true,
        position: "bottom",
        horizontalAlign: "center",
        fontSize: "11px",
        fontWeight: 500,
        offsetY: 5,
        markers: {
          width: 10,
          height: 10,
          radius: 2,
        },
        itemMargin: {
          horizontal: 8,
          vertical: 4,
        },
      },
      dataLabels: {
        enabled: true,
        style: {
          fontSize: "11px",
          fontWeight: "600",
          colors: ["#fff"],
        },
        formatter: function (val: number) {
          return Math.floor(val).toString();
        },
      },
      tooltip: {
        y: {
          formatter: function (val: number) {
            return Math.floor(val).toString() + " " + t("issues");
          },
          title: {
            formatter: function (seriesName: string) {
              return "";
            },
          },
        },
      },
      grid: {
        borderColor: "#f1f1f1",
        strokeDashArray: 4,
        xaxis: {
          lines: {
            show: true,
          },
        },
      },
    }),
    [skillChartData.labels]
  );

  const tagBarOptions: ApexOptions = useMemo(
    () => ({
      chart: {
        type: "bar",
        toolbar: { show: false },
        width: "100%",
        fontFamily: "inherit",
      },
      plotOptions: {
        bar: {
          borderRadius: 10,
          distributed: true,
          columnWidth: "70%",
        },
      },
      colors: COLORS_CHART,
      xaxis: {
        categories: tagLabels,
        labels: {
          rotate: -45,
          style: {
            fontSize: "11px",
            fontWeight: 500,
          },
        },
      },
      yaxis: {
        labels: {
          style: {
            fontSize: "11px",
          },
          formatter: function (val: number) {
            return Math.floor(val).toString();
          },
        },
      },
      legend: {
        show: false,
      },
      dataLabels: {
        enabled: true,
        style: {
          fontSize: "11px",
          fontWeight: "600",
        },
      },
      tooltip: {
        y: {
          formatter: function (val: number) {
            return val.toString();
          },
          title: {
            formatter: function (seriesName: string) {
              return "";
            },
          },
        },
      },
      grid: {
        borderColor: "#f1f1f1",
        strokeDashArray: 4,
      },
    }),
    [tagLabels]
  );
  //@ts-ignore
  const assigneeWorkloadOptions: ApexOptions = useMemo(
    () => ({
      chart: {
        type: "bar",
        toolbar: { show: false },
        stacked: false,
        width: "100%",
        fontFamily: "inherit",
      },
      plotOptions: {
        bar: {
          borderRadius: 8,
          horizontal: false,
          columnWidth: "75%",
        },
      },
      colors: ["#FF6B6B", "#4CAF50"],
      xaxis: {
        categories: assigneeWorkloadData.categories,
        labels: {
          rotate: -45,
          style: {
            fontSize: "11px",
            fontWeight: 500,
          },
        },
      },
      yaxis: {
        title: {
          text: t("Hours"),
          style: {
            fontSize: "12px",
            fontWeight: 600,
          },
        },
        labels: {
          style: {
            fontSize: "11px",
          },
        },
      },
      legend: {
        show: true,
        position: "top",
        horizontalAlign: "right",
        fontSize: "13px",
        fontWeight: 600,
        offsetY: 0,
        markers: {
          width: 14,
          height: 14,
          radius: 4,
        },
        itemMargin: {
          horizontal: 10,
          vertical: 0,
        },
      },
      dataLabels: {
        enabled: false,
      },
      tooltip: {
        y: {
          formatter: (val: number) => `${val.toFixed(1)} hrs`,
        },
      },
      grid: {
        borderColor: "#f1f1f1",
        strokeDashArray: 4,
      },
      stroke: {
        show: true,
        width: 2,
        colors: ["transparent"],
      },
    }),
    [assigneeWorkloadData.categories]
  );
  //@ts-ignore
  const skillWorkloadOptions: ApexOptions = useMemo(
    () => ({
      chart: {
        type: "bar",
        toolbar: { show: false },
        stacked: false,
        width: "100%",
        fontFamily: "inherit",
      },
      plotOptions: {
        bar: {
          borderRadius: 8,
          horizontal: true,
          barHeight: "70%",
        },
      },
      colors: ["#FF6B6B", "#4CAF50"],
      xaxis: {
        title: {
          text: t("Hours"),
          style: {
            fontSize: "12px",
            fontWeight: 600,
          },
        },
        labels: {
          style: {
            fontSize: "11px",
          },
        },
      },
      yaxis: {
        categories: skillWorkloadData.categories,
        labels: {
          style: {
            fontSize: "11px",
            fontWeight: 500,
          },
        },
      },
      legend: {
        show: true,
        position: "top",
        horizontalAlign: "right",
        fontSize: "13px",
        fontWeight: 600,
        offsetY: 0,
        markers: {
          width: 14,
          height: 14,
          radius: 4,
        },
        itemMargin: {
          horizontal: 10,
          vertical: 0,
        },
      },
      dataLabels: {
        enabled: false,
      },
      tooltip: {
        y: {
          formatter: (val: number) => `${val.toFixed(1)} hrs`,
        },
      },
      grid: {
        borderColor: "#f1f1f1",
        strokeDashArray: 4,
        xaxis: {
          lines: {
            show: true,
          },
        },
      },
      stroke: {
        show: true,
        width: 2,
        colors: ["transparent"],
      },
    }),
    [skillWorkloadData.categories]
  );

  return (
    <Box>
      {/* Top KPI Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <KPICard
            title={t("Total Issues")}
            value={totalBugs}
            icon={<AssignmentIcon sx={{ fontSize: 22 }} />}
            color="#9C27B0"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KPICard
            title={t("Open Issues")}
            value={openBugs}
            icon={<ErrorIcon sx={{ fontSize: 22 }} />}
            color="#FF9800"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KPICard
            title={t("Closed Issues")}
            value={closedBugs}
            icon={<CheckCircleIcon sx={{ fontSize: 22 }} />}
            color="#4CAF50"
            trend={
              totalBugs > 0
                ? Number(((closedBugs / totalBugs) * 100).toFixed(0))
                : 0
            }
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KPICard
            title={t("Blocked Issues")}
            value={blockedBugs}
            icon={<BugReportIcon sx={{ fontSize: 22 }} />}
            color="#F44336"
          />
        </Grid>
      </Grid>

      {/* Secondary KPI Row */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <KPICard
            title={t("Time Spent")}
            value={scrumMetrics.totalTimeSpent}
            unit="hrs"
            icon={<AccessTimeIcon sx={{ fontSize: 22 }} />}
            color="#FF6B6B"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KPICard
            title={t("Estimated Time")}
            value={scrumMetrics.totalEstimated}
            unit="hrs"
            icon={<TimerIcon sx={{ fontSize: 22 }} />}
            color="#2196F3"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KPICard
            title={t("Avg. Resolution")}
            value={avgResolutionTime}
            unit="hrs"
            icon={<TimelineIcon sx={{ fontSize: 22 }} />}
            color="#00BCD4"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KPICard
            title={t("Assignees")}
            value={totalUsers}
            icon={<GroupIcon sx={{ fontSize: 22 }} />}
            color="#8E24AA"
          />
        </Grid>
      </Grid>

      {/* Main Charts Row */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {statusChartData.length > 0 && (
          <Grid item xs={12} sm={6} md={6} lg={3}>
            <ChartCard title={t("Tasks by Status")} height={280}>
              <Box sx={{ width: "100%", height: "100%" }}>
                {parentData?.chartWrapper ? (
                  <parentData.chartWrapper
                    options={mainPieOptions}
                    series={statusChartData}
                    type="donut"
                    height={250}
                    width="100%"
                  />
                ) : null}
              </Box>
            </ChartCard>
          </Grid>
        )}

        {severityChartData.length > 0 && (
          <Grid item xs={12} sm={6} md={6} lg={3}>
            <ChartCard title={t("Tasks by Severity")} height={280}>
              <Box sx={{ width: "100%", height: "100%" }}>
                {parentData?.chartWrapper ? (
                  <parentData.chartWrapper
                    options={severityBarOptions}
                    series={[{ name: "Count", data: severityChartData }]}
                    type="bar"
                    height={250}
                    width="100%"
                  />
                ) : null}
              </Box>
            </ChartCard>
          </Grid>
        )}

        {typeChartData.length > 0 && (
          <Grid item xs={12} sm={6} md={6} lg={3}>
            <ChartCard title={t("Tasks by Type")} height={280}>
              <Box sx={{ width: "100%", height: "100%" }}>
                {parentData?.chartWrapper ? (
                  <parentData.chartWrapper
                    options={typeBarOptions}
                    series={[{ name: "Count", data: typeChartData }]}
                    type="bar"
                    height={250}
                    width="100%"
                  />
                ) : null}
              </Box>
            </ChartCard>
          </Grid>
        )}

        {progressData.length > 0 && (
          <Grid item xs={12} sm={6} md={6} lg={3}>
            <ChartCard title={t("Project Progress")} height={280}>
              <Box sx={{ width: "100%", height: "100%" }}>
                {parentData?.chartWrapper ? (
                  <parentData.chartWrapper
                    options={progressOptions}
                    series={progressData}
                    type="donut"
                    height={250}
                    width="100%"
                  />
                ) : null}
              </Box>
            </ChartCard>
          </Grid>
        )}
      </Grid>

      {/* Workload Charts Row */}
      {(assigneeWorkloadData.categories.length > 0 ||
        skillWorkloadData.categories.length > 0) && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {assigneeWorkloadData.categories.length > 0 && (
            <Grid item xs={12} lg={6}>
              <ChartCard title={t("Team Workload Distribution")} height={320}>
                <Box sx={{ width: "100%", height: "100%" }}>
                  {parentData?.chartWrapper ? (
                    <parentData.chartWrapper
                      options={assigneeWorkloadOptions}
                      series={[
                        {
                          name: t("Time Spent"),
                          data: assigneeWorkloadData.timeSpent,
                        },
                        {
                          name: t("Estimated"),
                          data: assigneeWorkloadData.estimated,
                        },
                      ]}
                      type="bar"
                      height={280}
                      width="100%"
                    />
                  ) : null}
                </Box>
              </ChartCard>
            </Grid>
          )}

          {skillWorkloadData.categories.length > 0 && (
            <Grid item xs={12} lg={6}>
              <ChartCard title={t("Skill-based Workload")} height={320}>
                <Box sx={{ width: "100%", height: "100%" }}>
                  {parentData?.chartWrapper ? (
                    <parentData.chartWrapper
                      options={skillWorkloadOptions}
                      series={[
                        {
                          name: t("Time Spent"),
                          data: skillWorkloadData.timeSpent,
                        },
                        {
                          name: t("Estimated"),
                          data: skillWorkloadData.estimated,
                        },
                      ]}
                      type="bar"
                      height={280}
                      width="100%"
                    />
                  ) : null}
                </Box>
              </ChartCard>
            </Grid>
          )}
        </Grid>
      )}

      {/* Additional Charts */}
      {(skillChartData.values.length > 0 || tagChartData.length > 0) && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {skillChartData.values.length > 0 && (
            <Grid item xs={12} lg={6}>
              <ChartCard title={t("Issues by Required Skill")} height={320}>
                <Box sx={{ width: "100%", height: "100%" }}>
                  {parentData?.chartWrapper ? (
                    <parentData.chartWrapper
                      options={skillBarOptions}
                      series={[{ name: "Issues", data: skillChartData.values }]}
                      type="bar"
                      height={280}
                      width="100%"
                    />
                  ) : null}
                </Box>
              </ChartCard>
            </Grid>
          )}

          {tagChartData.length > 0 && (
            <Grid item xs={12} lg={6}>
              <ChartCard title={t("Top Tags")} height={320}>
                <Box sx={{ width: "100%", height: "100%" }}>
                  {parentData?.chartWrapper ? (
                    <parentData.chartWrapper
                      options={tagBarOptions}
                      series={[{ name: "Count", data: tagChartData }]}
                      type="bar"
                      height={280}
                      width="100%"
                    />
                  ) : null}
                </Box>
              </ChartCard>
            </Grid>
          )}
        </Grid>
      )}

      {/* Sprint Details Section */}
      {sprints.length > 0 && (
        <Box sx={{ mt: 4 }}>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
            {t("Sprint Details")}
          </Typography>
          <Tabs
            value={selectedSprintIndex}
            onChange={(e, newValue) => setSelectedSprintIndex(newValue)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ mb: 2 }}
          >
            {sprints.map((sprint, index) => (
              <Tab
                key={sprint._id}
                label={
                  sprint.sprintWiseSummary.sprintSummary.currentSprint?.name ||
                  `${t("Sprint")} ${index + 1}`
                }
              />
            ))}
          </Tabs>
          {sprints[selectedSprintIndex] && (
            <SprintDetailView
              sprint={sprints[selectedSprintIndex]}
              parentData={parentData}
            />
          )}
        </Box>
      )}
    </Box>
  );
};

export default ScrumDashboard;
